import styled from 'styled-components'

import {
  labelPointFor,
  MarkupEditorAnnotation,
  MarkupLabel,
} from './MarkupEditorAnnotation'
import type { DisplaySize } from './markupEditorCoordinates'
import { geometryBounds } from './markupGeometry'
import { placeMarkupLabel } from './markupLabelPlacement'
import type {
  AnnotationGeometry,
  MarkupAnnotation,
  MarkupPoint,
  MarkupRegion,
} from './types'
import type { useMarkupEditorInteraction } from './useMarkupEditorInteraction'

const Layer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  .markup-rnd-annotation {
    outline: none;
  }

  .markup-rnd-annotation .markup-resize-handle-wrapper {
    pointer-events: auto;
  }

  .markup-rnd-annotation [data-markup-resize-handle] {
    width: 100%;
    height: 100%;
    padding: 0;
    position: relative;
    border: 0;
    background: transparent;
    cursor: inherit;
    outline: none;
  }

  .markup-rnd-annotation [data-markup-resize-handle]::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0.5rem;
    height: 0.5rem;
    box-sizing: border-box;
    border: 0.0625rem solid currentColor;
    border-radius: 0.125rem;
    background: ${({ theme }) => theme.forecast.background1};
    content: '';
    pointer-events: none;
    transform: translate(-50%, -50%);
  }

  .markup-rnd-annotation [data-markup-resize-handle]:focus-visible::after {
    outline: 0.125rem solid currentColor;
    outline-offset: 0.125rem;
  }
`

const LabelLayer = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
`

const polygonPoints = (vertices: ReadonlyArray<MarkupPoint>) =>
  vertices.map(({ x, y }) => `${x},${y}`).join(' ')

const renderBoundary = (
  boundary: AnnotationGeometry,
  regionId: string,
  color: string
) => {
  if (boundary.shape === 'point') return null
  if (boundary.shape === 'rectangle')
    return (
      <rect
        data-region-id={regionId}
        data-zone-id={regionId}
        fill='none'
        height={boundary.y_max - boundary.y_min}
        stroke={color}
        strokeDasharray='8 6'
        strokeWidth={2}
        vectorEffect='non-scaling-stroke'
        width={boundary.x_max - boundary.x_min}
        x={boundary.x_min}
        y={boundary.y_min}
      />
    )
  return (
    <polygon
      data-region-id={regionId}
      data-zone-id={regionId}
      fill='none'
      points={polygonPoints(boundary.vertices)}
      stroke={color}
      strokeDasharray='8 6'
      strokeWidth={2}
      vectorEffect='non-scaling-stroke'
    />
  )
}

type EditorInteraction = ReturnType<typeof useMarkupEditorInteraction>
const annotationFor = (
  annotations: ReadonlyArray<MarkupAnnotation>,
  target: Element
) =>
  annotations.find(
    (annotation) => annotation.id === target.getAttribute('data-annotation-id')
  )

interface MarkupEditorOverlayProps {
  regions: ReadonlyArray<MarkupRegion>
  annotations: ReadonlyArray<MarkupAnnotation>
  selectedAnnotationId?: string
  interaction: EditorInteraction
  display: DisplaySize
  getPageBounds: () =>
    | Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>
    | undefined
}

export function MarkupEditorOverlay({
  regions,
  annotations,
  selectedAnnotationId,
  interaction,
  display,
  getPageBounds,
}: MarkupEditorOverlayProps) {
  return (
    <Layer
      aria-label='Document markup annotations'
      onFocusCapture={(event) => {
        const target = event.target
        if (!(target instanceof Element)) return
        const annotation = annotationFor(annotations, target)
        if (!annotation) return
        const vertex = target.getAttribute('data-edit-vertex')
        if (vertex !== null)
          interaction.focusAnnotation(annotation, Number(vertex))
        else if (
          target.getAttribute('data-annotation-shape') === 'true' ||
          target.hasAttribute('data-edge-index') ||
          target.hasAttribute('data-edit-handle')
        )
          interaction.focusAnnotation(annotation)
      }}
      onMouseDownCapture={(event) => {
        const shape =
          event.target instanceof Element &&
          event.target.closest('[data-annotation-shape="true"]')
        if (!(shape instanceof SVGElement)) return
        const annotation = annotationFor(annotations, shape)
        if (!annotation) return
        const wasFocused = document.activeElement === shape
        shape.focus({ preventScroll: true })
        if (wasFocused) interaction.selectAnnotation(annotation.id)
      }}
      onTouchStartCapture={(event) => {
        const shape =
          event.target instanceof Element &&
          event.target.closest('[data-annotation-shape="true"]')
        if (!(shape instanceof SVGElement)) return
        const annotation = annotationFor(annotations, shape)
        if (annotation) interaction.focusAnnotation(annotation)
      }}
      onTouchCancel={interaction.cancelTouchGesture}>
      <LabelLayer
        aria-hidden='true'
        preserveAspectRatio='none'
        viewBox='0 0 1 1'>
        {regions.map((region) => {
          if (!region.boundary || region.boundary.shape === 'point') return null
          const placement = placeMarkupLabel(
            labelPointFor(region.boundary),
            region.label,
            display,
            'zone',
            geometryBounds(region.boundary)
          )
          return (
            <g key={`region-${region.id}`}>
              <MarkupLabel
                label={region.label}
                placement={placement}
                color={region.color}
                weight={600}
              />
              {renderBoundary(region.boundary, region.id, region.color)}
            </g>
          )
        })}
      </LabelLayer>
      {annotations.map((annotation) => (
        <MarkupEditorAnnotation
          key={annotation.id}
          annotation={annotation}
          selected={annotation.id === selectedAnnotationId}
          display={display}
          getPageBounds={getPageBounds}
          interaction={interaction}
        />
      ))}
    </Layer>
  )
}
